import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useSync } from '../context/SyncContext';
import { saveContext } from '../lib/db';
import Breadcrumbs from '../components/Breadcrumbs';
import StatusTile from '../components/StatusTile';
import SiteActionSheet from '../components/SiteActionSheet';
import InspectionBanner from '../components/InspectionBanner';
import { StatCard, StatusBar, StatChips, damageTone } from '../components/metrics';
import { Boxes, Loader2, Layers, TriangleAlert, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { POSITIONS } from '../lib/status';
import { toast } from 'sonner';

const ALL_POS = ['poster_left', 'poster_right', 'door_vinyl', 'door_side'];

export default function LiftDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { pendingCount } = useSync();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inspection = params.get('inspection');
  const inspQ = inspection ? `?inspection=${inspection}` : '';
  const canEdit = ['admin', 'manager'].includes(user.role);
  const [data, setData] = useState(null);
  const [activeSite, setActiveSite] = useState(null);
  const [newPos, setNewPos] = useState('poster_left');
  const [inspMode, setInspMode] = useState('inspect');

  useEffect(() => {
    if (inspection) api.get(`/inspections/${inspection}`).then((r) => setInspMode(r.data.mode || 'inspect')).catch(() => {});
  }, [inspection]);

  const load = useCallback(() => {
    const url = `/lifts/${id}/detail${inspection ? `?inspection_id=${inspection}` : ''}`;
    return api.get(url).then((r) => {
      setData(r.data);
      saveContext(user.id, { societyId: r.data.society.id, towerId: r.data.tower.id, liftId: r.data.lift.id });
    });
  }, [id, inspection, user.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('sync-updated', h);
    return () => window.removeEventListener('sync-updated', h);
  }, [load]);

  const openTile = (site) => {
    if (user.role === 'operator') setActiveSite(site.id);
    else navigate(`/sites/${site.id}`);
  };
  const addSite = async () => { await api.post(`/lifts/${id}/sites`, { position_type: newPos }); load(); toast.success('Site added'); };
  const delSite = async (s) => { if (!window.confirm(`Delete ${POSITIONS[s.position_type]} and its history?`)) return; await api.delete(`/sites/${s.id}`); load(); toast.success('Site deleted'); };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const { lift, tower, society, stats, sites } = data;

  return (
    <div className="space-y-5">
      {inspection && <InspectionBanner inspectionId={inspection} />}
      <Breadcrumbs items={[{ label: 'Societies', to: '/societies' }, { label: society.name, to: `/societies/${society.id}${inspQ}` }, { label: tower.name, to: `/towers/${tower.id}${inspQ}` }, { label: lift.name }]} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline text-primary flex items-center gap-2"><Boxes className="h-3.5 w-3.5" /> {society.name} · {tower.name}</p>
          <h1 className="font-head text-3xl font-bold">{lift.name}</h1>
        </div>
        {inspection && <span className="rounded-full border border-sky-600 bg-sky-950/60 px-3 py-1 text-xs text-sky-300">Inspection mode</span>}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Sites" value={stats.total} icon={Layers} />
        <StatCard label="Occupancy" value={`${stats.occupancy_pct}%`} icon={Boxes} tone="text-blue-400" />
        <StatCard label="Damaged" value={stats.damaged} icon={TriangleAlert} tone={damageTone(stats.damage_pct)} />
        <StatCard label="Damage" value={`${stats.damage_pct}%`} tone={damageTone(stats.damage_pct)} />
      </div>
      <div className="rounded-xl border border-border bg-card p-4">
        <StatusBar counts={stats.status_counts} total={stats.total} className="mb-3" />
        <StatChips counts={stats.status_counts} />
      </div>

      {canEdit && !inspection && (
        <div className="flex items-center gap-2">
          <Select value={newPos} onValueChange={setNewPos}>
            <SelectTrigger data-testid="ld-new-pos" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{ALL_POS.map((p) => <SelectItem key={p} value={p}>{POSITIONS[p]}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="secondary" onClick={addSite} data-testid="ld-add-site"><Plus className="mr-1 h-4 w-4" /> Add site position</Button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {sites.map((s) => (
          <div key={s.id} className="relative rise">
            <StatusTile site={s} onClick={() => openTile(s)} />
            {canEdit && !inspection && (
              <button data-testid={`ld-del-site-${s.id}`} onClick={() => delSite(s)} className="absolute bottom-2 right-2 rounded-md bg-rose-600/80 p-1 text-white hover:bg-rose-500"><Trash2 className="h-3 w-3" /></button>
            )}
          </div>
        ))}
        {!sites.length && <p className="col-span-full py-8 text-center text-sm text-muted-foreground">No site positions{inspection ? ' in this inspection' : ''}.</p>}
      </div>

      <SiteActionSheet siteId={activeSite} open={!!activeSite} mode={inspMode} inspectionId={inspection} readOnly={['client', 'society_manager'].includes(user.role)} onClose={(changed) => { setActiveSite(null); if (changed) load(); }} />
    </div>
  );
}
